import { useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { Check, Copy, Loader2 } from "lucide-react";
import { Button } from "@ndd/ui";
import { authenticatedApi } from "@/lib/api";

interface TranscriptPanelProps {
  sessionId: string;
}

export function TranscriptPanel({ sessionId }: TranscriptPanelProps) {
  const { getToken } = useAuth();
  const [transcript, setTranscript] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    authenticatedApi<{ data: { transcript: string | null } }>(
      getToken,
      `/api/sessions/${sessionId}/transcript`,
    )
      .then((body) => {
        if (cancelled) return;
        setTranscript(body.data.transcript);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, getToken]);

  function handleCopy() {
    if (!transcript) return;
    navigator.clipboard.writeText(transcript);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="flex h-full flex-col bg-sand-50">
      <div className="flex items-center justify-between border-b border-sand-200 bg-card px-4 py-2">
        <span className="text-xs font-bold uppercase tracking-wider text-foreground">
          Transcript
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={handleCopy}
          disabled={!transcript}
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 text-forest-500" />
          ) : (
            <Copy className="h-3.5 w-3.5 text-sand-500" />
          )}
          <span className="sr-only">Copy transcript</span>
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-sand-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading transcript...
          </div>
        )}
        {!loading && error && (
          <p className="text-sm text-error">Failed to load transcript.</p>
        )}
        {!loading && !error && !transcript && (
          <p className="text-sm text-sand-500">No transcript available.</p>
        )}
        {!loading && !error && transcript && (
          <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-foreground">
            {transcript}
          </pre>
        )}
      </div>
    </div>
  );
}
